import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface PaymentMethodsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userType: 'supabase' | 'internal';
}

const PaymentMethodsModal: React.FC<PaymentMethodsModalProps> = ({ 
  isOpen, 
  onClose, 
  userType 
}) => {
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: '1',
      type: 'visa',
      last4: '4242',
      expiryDate: '12/25',
      isDefault: true
    },
    {
      id: '2',
      type: 'mastercard',
      last4: '5555',
      expiryDate: '09/26',
      isDefault: false
    }
  ]);

  const [showAddNew, setShowAddNew] = useState(false);
  const [newCard, setNewCard] = useState({
    number: '',
    expiry: '',
    cvc: '',
    name: ''
  });

  const handleAddPaymentMethod = () => {
    if (!newCard.number || !newCard.expiry || !newCard.cvc || !newCard.name) {
      toast.error('Please fill in all card details');
      return;
    }

    const newMethod = {
      id: Date.now().toString(),
      type: 'visa',
      last4: newCard.number.slice(-4),
      expiryDate: newCard.expiry,
      isDefault: paymentMethods.length === 0
    };

    setPaymentMethods([...paymentMethods, newMethod]);
    setNewCard({ number: '', expiry: '', cvc: '', name: '' });
    setShowAddNew(false);
    toast.success('Payment method added successfully');
  };

  const handleRemovePaymentMethod = (id: string) => {
    setPaymentMethods(paymentMethods.filter(method => method.id !== id));
    toast.success('Payment method removed');
  };

  const handleSetDefault = (id: string) => {
    setPaymentMethods(paymentMethods.map(method => ({
      ...method,
      isDefault: method.id === id
    })));
    toast.success('Default payment method updated');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">Payment Methods</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Existing Payment Methods */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Your Payment Methods</h3>
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <Card key={method.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-8 w-8 text-sports-green" />
                        <div>
                          <p className="text-white font-medium">
                            **** **** **** {method.last4}
                          </p>
                          <p className="text-gray-400 text-sm">
                            Expires {method.expiryDate}
                          </p>
                          {method.isDefault && (
                            <span className="text-sports-green text-xs">Default</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!method.isDefault && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSetDefault(method.id)}
                            className="border-gray-600 text-gray-300 hover:bg-gray-700"
                          >
                            Set Default
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemovePaymentMethod(method.id)}
                          className="border-red-600 text-red-400 hover:bg-red-900/20"
                          disabled={method.isDefault && paymentMethods.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Add New Payment Method */}
          {!showAddNew ? (
            <Button
              onClick={() => setShowAddNew(true)}
              className="w-full bg-sports-green text-black hover:bg-green-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Payment Method
            </Button>
          ) : (
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Add New Card</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="card-name" className="text-gray-300">Cardholder Name</Label>
                  <Input
                    id="card-name"
                    value={newCard.name}
                    onChange={(e) => setNewCard({...newCard, name: e.target.value})}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="card-number" className="text-gray-300">Card Number</Label>
                  <Input
                    id="card-number"
                    value={newCard.number}
                    onChange={(e) => setNewCard({...newCard, number: e.target.value})}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="expiry" className="text-gray-300">Expiry Date</Label>
                    <Input
                      id="expiry"
                      value={newCard.expiry}
                      onChange={(e) => setNewCard({...newCard, expiry: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="MM/YY"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <Label htmlFor="cvc" className="text-gray-300">CVC</Label>
                    <Input
                      id="cvc"
                      value={newCard.cvc}
                      onChange={(e) => setNewCard({...newCard, cvc: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="123"
                      maxLength={4}
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleAddPaymentMethod}
                    className="bg-sports-green text-black hover:bg-green-600"
                  >
                    Add Card
                  </Button>
                  <Button
                    onClick={() => setShowAddNew(false)}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-gray-500 text-center">
            {userType === 'supabase' ? 'Old School account payment methods' : 'New School account payment methods'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodsModal;
